import { z } from "zod";
import { ConsoleAccessContextV1, ConsoleLocale, ConsoleRole, OperatorNameV1 } from "../ports.ts";
import { TotpCode } from "./auth.ts";

/** IANA time zone name (e.g. `America/Sao_Paulo`); rejected when the runtime does not know it. */
export const TimeZoneName = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z][A-Za-z0-9_+\-/]*$/)
  .refine((name) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone: name });
      return true;
    } catch {
      return false;
    }
  }, "unknown time zone")
  .meta({ id: "TimeZoneName" });

/** Display theme of the Explorer (design D22): `dark` (default) or `light`. */
export const DisplayTheme = z.enum(["dark", "light"]).meta({ id: "DisplayTheme" });
export type DisplayTheme = z.infer<typeof DisplayTheme>;

/** Unicode control characters (C0, DEL and C1): never part of a display name. */
const CONTROL_CHARACTER = /\p{Cc}/u;

/**
 * Display name a console user sets in Settings (change add-console-account-settings, design D2):
 * 1 to 80 characters (Unicode code points) after trimming, no control characters. Employee data kept
 * only in `iam`; never logged.
 */
export const DisplayNameV1 = z
  .string()
  .max(400)
  .refine((value) => {
    const length = Array.from(value.trim()).length;
    return length >= 1 && length <= 80 && !CONTROL_CHARACTER.test(value);
  }, "1 to 80 characters after trimming, no control characters")
  .meta({ id: "DisplayNameV1" });

/** The platform-level role of the session's console user (`null` for an operator user). */
export const PlatformRoleV1 = z.enum(["platform_administrator"]).meta({ id: "PlatformRoleV1" });

/**
 * `GET /explorer/v1/me` (design D4): the `ConsoleAccessContextV1` of the session plus the display
 * time zone and theme, the platform role and the display name when set (the user menu shows the
 * name and its initials, design D11). Authenticated, no permission code. No email.
 */
export const MeResponseV1 = z
  .object({
    ...ConsoleAccessContextV1.shape,
    /**
     * Per allowed operator, the access context plus the operator's registered name (from the
     * Platform area, through `operator-auth`); `operator_name` is absent when the name cannot be
     * read, and the console then labels the operator by its short id.
     */
    operators: z.array(
      ConsoleAccessContextV1.shape.operators.element
        .extend({ operator_name: OperatorNameV1.optional() })
        .strict(),
    ),
    time_zone: TimeZoneName,
    theme: DisplayTheme,
    platform_role: PlatformRoleV1.nullable(),
    display_name: DisplayNameV1.optional(),
  })
  .strict()
  .meta({ id: "MeResponseV1" });
export type MeResponseV1 = z.infer<typeof MeResponseV1>;

/** `PUT /explorer/v1/me/settings`: stored per user in `iam` (`console_users.locale`, time zone, theme). */
export const UpdateMySettingsRequestV1 = z
  .object({
    locale: ConsoleLocale.optional(),
    time_zone: TimeZoneName.optional(),
    theme: DisplayTheme.optional(),
  })
  .strict()
  .refine((value) => Object.values(value).some((setting) => setting !== undefined), {
    message: "at least one setting is required",
  })
  .meta({ id: "UpdateMySettingsRequestV1" });
export type UpdateMySettingsRequestV1 = z.infer<typeof UpdateMySettingsRequestV1>;

export const MySettingsResponseV1 = z
  .object({ locale: ConsoleLocale, time_zone: TimeZoneName, theme: DisplayTheme })
  .strict()
  .meta({ id: "MySettingsResponseV1" });
export type MySettingsResponseV1 = z.infer<typeof MySettingsResponseV1>;

// ---- Settings: the session's own account (change add-console-account-settings, design D1–D5) ----

/** How the console user signs in: a password, the organization's identity provider, or both. */
export const SignInKindV1 = z.enum(["password", "sso", "both"]).meta({ id: "SignInKindV1" });

/** Two-factor state of the account: none, an authenticator, or reset and waiting for recovery. */
export const TwoFactorStateV1 = z
  .enum(["off", "on", "reset_pending"])
  .meta({ id: "TwoFactorStateV1" });

/**
 * `GET /explorer/v1/me/account`: the Settings profile of the session's console user. Kept apart from
 * `/me` so the email stays out of the shell's cached session object (design D1). The email is
 * read-only (design D3).
 */
export const AccountV1 = z
  .object({
    email: z.email(),
    display_name: DisplayNameV1.nullable(),
    /** `null` for a Platform Administrator, who belongs to no organization. */
    organization: z
      .object({ organization_id: z.uuid(), name: z.string().min(1).max(200) })
      .strict()
      .nullable(),
    operators: z.array(
      z
        .object({
          operator_id: z.uuid(),
          operator_name: OperatorNameV1.optional(),
          role: ConsoleRole,
        })
        .strict(),
    ),
    platform_role: PlatformRoleV1.nullable(),
    sign_in: SignInKindV1,
    two_factor: TwoFactorStateV1,
    /** Two-factor authentication is mandatory for this user's password logins (design D5). */
    two_factor_required: z.boolean(),
    /**
     * The installation offers the own two-factor set-up in Settings (release flag
     * `iam.two-factor`); the console hides the set-up while it is off (design D6, rollback).
     */
    two_factor_available: z.boolean(),
  })
  .strict()
  .meta({ id: "AccountV1" });
export type AccountV1 = z.infer<typeof AccountV1>;

/** `PUT /explorer/v1/me/profile`: sets (or, with `null`, clears) the display name. No email. */
export const ProfileUpdateV1 = z
  .object({ display_name: DisplayNameV1.nullable() })
  .strict()
  .meta({ id: "ProfileUpdateV1" });
export type ProfileUpdateV1 = z.infer<typeof ProfileUpdateV1>;

/** A password field of a request: passed through to the identity store once, never stored or logged. */
const PasswordField = z.string().min(1).max(256);

/**
 * `POST /explorer/v1/me/password` (design D4): the current password, the current code when
 * two-factor authentication is on, and the new password (console password policy).
 */
export const PasswordChangeV1 = z
  .object({
    current_password: PasswordField,
    code: TotpCode.optional(),
    new_password: PasswordField,
  })
  .strict()
  .meta({ id: "PasswordChangeV1" });
export type PasswordChangeV1 = z.infer<typeof PasswordChangeV1>;

/** `POST /explorer/v1/me/two-factor/begin`: the password confirms the set-up of an authenticator. */
export const TwoFactorEnrollV1 = z
  .object({ password: PasswordField })
  .strict()
  .meta({ id: "TwoFactorEnrollV1" });
export type TwoFactorEnrollV1 = z.infer<typeof TwoFactorEnrollV1>;

/**
 * `POST /explorer/v1/me/two-factor/replace` (owner decision O-11): the password and a current code
 * of the old authenticator start the set-up of a new one.
 */
export const TwoFactorReplaceV1 = z
  .object({ password: PasswordField, code: TotpCode })
  .strict()
  .meta({ id: "TwoFactorReplaceV1" });
export type TwoFactorReplaceV1 = z.infer<typeof TwoFactorReplaceV1>;

/** `POST /explorer/v1/me/two-factor/disable`: the password and a current code remove an optional factor. */
export const TwoFactorDisableV1 = z
  .object({ password: PasswordField, code: TotpCode })
  .strict()
  .meta({ id: "TwoFactorDisableV1" });
export type TwoFactorDisableV1 = z.infer<typeof TwoFactorDisableV1>;
